import type { FunctionComponent, HTMLProps } from 'react'
import React from 'react'

import classnames from 'classnames'

import { WarningIcon } from '../Icons/Miscellaneous/Warning'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Badge.module.scss'

export const Badge: FunctionComponent<BadgeProps> = ({
  text,
  isSquared = false,
  theme,
  className = '',
  ...rest
}) => {
  if (!theme) {
    return null
  }

  const themeStyle = theme
    .replace(
      /(\w)(\w*)/g,
      (_g0: string, g1: string, g2: string) =>
        g1.toUpperCase() + g2.toLowerCase()
    )
    .replaceAll(' ', '')

  const badgeMapText = badgeTextMapper[themeStyle]
  const badgeText = text || badgeMapText
  const badgeStyle = `badge${themeStyle}`
  const displayIcon = [
    'safety alert',
    'brand alert',
    'security notice',
  ].includes(theme)

  const allowedSquaredThemes = [
    'brand alert',
    'safety alert',
    'lab tested',
    'security notice',
    'top story',
  ]

  const squareBadge = isSquared && allowedSquaredThemes.includes(theme)

  return (
    <div
      className={classnames(
        styles.badge,
        styles[badgeStyle],
        {
          [styles.badgeSquared]: squareBadge,
        },
        className
      )}
      data-testid="sb-badge"
      {...rest}
    >
      {displayIcon && <WarningIcon width={24} data-testid="badge-icon" />}
      <TypographyV2
        className={styles.label}
        tag="span"
        textStyle="sb-text-component-badge"
      >
        {badgeText}
      </TypographyV2>
    </div>
  )
}

///////// IMPLEMENTATION /////////

export type BadgeProps = {
  className?: string
  text?: string
  theme?: BadgeVariant
  /**
   * isSquared Type
   * @note `isSquared` is only available for the folowing themes: 'brand alert', 'safety alert', 'lab tested', 'security notice', 'top story'`
   */
  isSquared?: boolean
  [key: `data-${string}`]: string | undefined
} & HTMLProps<HTMLDivElement>

export type BadgeTheme = BadgeVariant

export const validBadges: BadgeVariant[] = [
  'best buy',
  'dont buy',
  'eco buy',
  'first look',
  'great value',
  'brand alert',
  'safety alert',
  'lab tested',
  'security notice',
  'top story',
  'default',
]

export type BadgeVariant =
  | 'best buy'
  | 'dont buy'
  | 'eco buy'
  | 'first look'
  | 'great value'
  | 'brand alert'
  | 'safety alert'
  | 'lab tested'
  | 'security notice'
  | 'top story'
  | 'default'

const badgeTextMapper: { [key: string]: string } = {
  BestBuy: 'best buy',
  Default: "I'm a badge",
  DontBuy: "don't buy",
  EcoBuy: 'eco buy',
  FirstLook: 'first look',
  GreatValue: 'great value',
  TopStory: 'top story',
  LabTested: 'lab tested',
  BrandAlert: 'brand alert',
  SafetyAlert: 'safety alert',
  SecurityNotice: 'security notice',
}
